import { TransactionCategory, TransactionType } from './transaction';
import { FrequentTransaction, WarningIndicator } from './bank-statements';

export interface OverdraftIndicator {
  days: number;
  maxAmount: number;
}

export interface CashFlowCategoryIndicator {
  category: TransactionCategory[];
  count: number;
  amount: number;
}

export interface AverageMoneyIndicator {
  averageNumber: number;
  averageMoney: number;
}

export interface CashFlowTypeIndicator {
  type: TransactionType[];
  count: number;
  amount: number;
}

export interface ExplicitIndicatorValues {
  averageMoneyIndicator?: AverageMoneyIndicator;
  checkUsage?: CashFlowCategoryIndicator;
  cardType?: 'DIFFERED' | 'DIRECT' | 'NONE';
  presenceOfANegativeBalance?: boolean;
  overdraftFees?: CashFlowCategoryIndicator;
  interventionFees?: CashFlowCategoryIndicator;
  bankIncidentFees?: CashFlowCategoryIndicator;
  internalTransfer?: CashFlowTypeIndicator;
  paymentRejection?: number;
  checkRejection?: number;
  telecomExpenditure?: number;
  locDrawdownsOfThePast30Days?: CashFlowCategoryIndicator;
}

export interface DangerCashFlowIndicatorValues {
  bankIncidentFees?: CashFlowCategoryIndicator;
  checkRejection?: CashFlowCategoryIndicator;
  checkRejectionFees?: CashFlowCategoryIndicator;
  gambling?: CashFlowCategoryIndicator;
  interventionFees?: CashFlowCategoryIndicator;
  locDrawdown?: CashFlowCategoryIndicator;
  overdraftFees?: CashFlowCategoryIndicator;
  paymentRejection?: CashFlowCategoryIndicator;
  paymentRejectionFees?: CashFlowCategoryIndicator;
  loanPaymentRejection?: CashFlowCategoryIndicator;
  debtCollection?: CashFlowCategoryIndicator;
  provisionDirectDebtRecovery?: CashFlowCategoryIndicator;
}

export interface DebtCollectionIndicatorValues {
  dangerCashFlowIndicatorValues?: DangerCashFlowIndicatorValues;
  didBankAccountSeizureEventOccur: boolean | WarningIndicator;
  didThirdPartyHolderNoticeEventOccur: boolean | WarningIndicator;
  overdraftIndicatorValue?: OverdraftIndicator;
  internationalTransfers?: WarningIndicator;
  bankTransactionsToBeJustified?: WarningIndicator;
  internalTransfersWithoutRelatedTransactions?: WarningIndicator;
}

export type CofidisDebtCollectionIndicatorValues = DebtCollectionIndicatorValues;

export interface DebtConsolidationIndicatorValues {
  dangerCashFlowIndicatorValues?: DangerCashFlowIndicatorValues;
  didBankAccountSeizureEventOccur: boolean | WarningIndicator;
  didThirdPartyHolderNoticeEventOccur: boolean | WarningIndicator;
  overdraftIndicatorValue?: OverdraftIndicator;
  internationalTransfers?: WarningIndicator;
  bankTransactionsToBeJustified?: WarningIndicator;
  internalTransfersWithoutRelatedTransactions?: WarningIndicator;
}

export interface CofidisDebtConsolidationIndicatorValues extends DebtConsolidationIndicatorValues {
  explicitIndicatorValues?: ExplicitIndicatorValues;
  internationalTransfers?: WarningIndicator;
}

export interface DebtCollectionSynthesis {
  cashFlowIndicatorValues: DebtCollectionIndicatorValues;
  lastSavingsBalance: number;
  loanNumber: number;
  totalBalance: number;
  totalLocDrawdown: number;
  totalUnpaidDirectDebit: number;
}

export interface CofidisDebtCollectionSynthesis extends Omit<DebtCollectionSynthesis, 'cashFlowIndicatorValues'> {
  cashFlowIndicatorValues: CofidisDebtCollectionIndicatorValues;
}

export const paymentIndicatorTypes = [
  TransactionType.CASH_WITHDRAWAL,
  TransactionType.CHECK,
  TransactionType.DIRECT_DEBIT,
  TransactionType.OUTGOING_TRANSFER,
  TransactionType.CARD,
];

export type ComplexPaymentIndicatorType = (typeof paymentIndicatorTypes)[0];

export type PaymentMethodIndicator = ComplexPaymentIndicator;

export interface ComplexPaymentIndicator {
  type: (typeof paymentIndicatorTypes)[0];
  count: number;
  amount: number;
}

export interface DebtConsolidationLoanAnalysis {
  cashFlowIndicatorValues: DebtConsolidationIndicatorValues;
  checkingsAccountNumber: number;
  frequentTransactions: FrequentTransaction[];
  lastMonthCashburn: number;
  lastSavingsBalance: number;
  loansNumber: number;
  paymentIndicatorValues: {
    differedPaymentIndicator?: boolean;
  };
  paymentMethods: PaymentMethodIndicator[];
  savingsAccountNumber: number;
  totalBalance: number;
  totalMonthlyAmount: number;
}

export interface CofidisDebtConsolidationLoanAnalysis
  extends Omit<DebtConsolidationLoanAnalysis, 'cashFlowIndicatorValues'> {
  cashFlowIndicatorValues: CofidisDebtConsolidationIndicatorValues;
}
